import i18next from 'i18next';
import { useMutation } from 'react-query';
import { sleep } from '../../../config/utils/genericHelpers';
import { CONFIRM_DELIVERY_OUTCOME, PackagingDetailType } from '../../../interfaces/myDataDelivery.interface';
import { GenericResponse } from '../../../interfaces/shared.interface';
import { TRANSIT_STATUS_REFRESH_DELAY_MS } from '../../../services/utils/deliveryStatusUtils';
import { setToast } from '../../../store/core/coreSlice';
import { store } from '../../../store/store';
import { apiRQ } from '../../api';
import { queryClient } from '../../APIProvider';

interface DataDTO {
  qrUrl: string;
  outcome: CONFIRM_DELIVERY_OUTCOME;
  deliveredWithoutRecipient: boolean;
  /**
   * Required only when outcome === 'PARTIAL'
   */
  deliveredPackaging?: PackagingDetailType[];
  /**
   * Passing just to be saved in our db for reference
   */
  mark?: string | number;
}

const confirmDeliveryOutcomeService = async (data: DataDTO): Promise<GenericResponse> => {
  const resp = await apiRQ.post<GenericResponse>('/delivery/confirm-delivery', { data });

  // myDATA needs a moment to register the action before GetDeliveryNoteStatus reflects it -- wait
  // here (keeping the mutation, and any `isLoading`-driven spinner, active) before returning, so the
  // status refetch below doesn't race a stale read
  if (resp.data?.success) await sleep(TRANSIT_STATUS_REFRESH_DELAY_MS);

  return resp.data;
};

export const useMutateConfirmDeliveryOutcome = () => {
  return useMutation(confirmDeliveryOutcomeService, {
    onSuccess: (data, variables) => {
      if (data?.success) {
        store.dispatch(setToast({ value: { show: true, msg: i18next.t('DELIVERY.SUCCESSFUL_CONFIRM_OUTCOME') } }));
        // When the mark is already known, the parent screen owns the status display via
        // useDeliveryStatusByMark -- invalidate that instead of also firing a qrUrl-based fetch
        if (variables.mark) {
          queryClient?.invalidateQueries({ queryKey: ['useDeliveryStatusByMark', String(variables.mark)] });
        } else {
          queryClient?.invalidateQueries({ queryKey: ['useDeliveryStatusByQrUrl', variables.qrUrl] });
        }
      }
    },
  });
};
